import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Box,
  Tabs,
  Tab
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
  dialogContent: {
    minWidth: '600px',
    maxWidth: '800px',
  },
  sectionTitle: {
    marginTop: '16px',
    marginBottom: '8px',
    fontWeight: 'bold',
  },
  representativeItem: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    marginBottom: '8px',
  },
  powerItem: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    marginBottom: '8px',
  },
  tabContent: {
    paddingTop: '16px',
  },
}));

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function UserDetailModal({ open, onClose, user }) {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);

  if (!user) return null;

  const isLegalEntity = user.userType === 'juridica';

  // Mock data for representatives and powers
  const mockRepresentatives = isLegalEntity ? [
    {
      id: 1,
      name: 'María González',
      rut: '98.765.432-1',
      email: 'maria.gonzalez@email.com',
      legalAgent: true
    },
    {
      id: 2,
      name: 'Carlos Rodríguez',
      rut: '87.654.321-2',
      email: 'carlos.rodriguez@email.com',
      legalAgent: false
    }
  ] : [];

  const mockPowers = isLegalEntity ? [
    {
      id: 1,
      fileName: 'Poder_General_2024.pdf',
      uploadDate: '2024-01-15',
      size: '1.2 MB'
    },
    {
      id: 2,
      fileName: 'Poder_Especial_Tramites.pdf',
      uploadDate: '2024-02-10',
      size: '850 KB'
    }
  ] : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {isLegalEntity ? <BusinessIcon /> : <PersonIcon />}
          <Typography variant="h6">
            Detalles del Usuario - {user.name}
          </Typography>
          <Chip
            label={isLegalEntity ? 'Persona Jurídica' : 'Persona Natural'}
            color={isLegalEntity ? 'secondary' : 'primary'}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Información Básica" />
          {isLegalEntity && <Tab label="Representantes" />}
          {isLegalEntity && <Tab label="Poderes" />}
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <div className={classes.tabContent}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Nombre:
                </Typography>
                <Typography variant="body1">{user.name}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  RUT:
                </Typography>
                <Typography variant="body1">{user.rut}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Email:
                </Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Teléfono:
                </Typography>
                <Typography variant="body1">{user.phone || 'No especificado'}</Typography>
              </Grid>
              {isLegalEntity && user.address && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Dirección:
                  </Typography>
                  <Typography variant="body1">{user.address}</Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Estado:
                </Typography>
                <Chip
                  label={user.status === 'active' ? 'Activo' : 'Inactivo'}
                  color={user.status === 'active' ? 'success' : 'default'}
                  size="small"
                />
              </Grid>
            </Grid>
          </div>
        </TabPanel>

        {isLegalEntity && (
          <TabPanel value={tabValue} index={1}>
            <div className={classes.tabContent}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Representantes</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {/* TODO: Open add representative modal */}}
                >
                  Agregar Representante
                </Button>
              </Box>
              
              {mockRepresentatives.length === 0 ? (
                <Paper style={{ padding: '20px', textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No hay representantes registrados
                  </Typography>
                </Paper>
              ) : (
                <List>
                  {mockRepresentatives.map((rep) => (
                    <ListItem key={rep.id} className={classes.representativeItem}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1">{rep.name}</Typography>
                            {rep.legalAgent && (
                              <Chip label="Apoderado Legal" color="primary" size="small" />
                            )}
                          </Box>
                        }
                        secondary={
                          <div>
                            <Typography variant="body2">RUT: {rep.rut}</Typography>
                            <Typography variant="body2">Email: {rep.email}</Typography>
                          </div>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => {/* TODO: Delete representative */}}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          </TabPanel>
        )}

        {isLegalEntity && (
          <TabPanel value={tabValue} index={2}>
            <div className={classes.tabContent}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Archivos de Poderes</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {/* TODO: Open upload power modal */}}
                >
                  Subir Poder
                </Button>
              </Box>

              {mockPowers.length === 0 ? (
                <Paper style={{ padding: '20px', textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No hay archivos de poderes registrados
                  </Typography>
                </Paper>
              ) : (
                <List>
                  {mockPowers.map((power) => (
                    <ListItem key={power.id} className={classes.powerItem}>
                      <ListItemText
                        primary={power.fileName}
                        secondary={
                          <div>
                            <Typography variant="body2">
                              Subido: {power.uploadDate}
                            </Typography>
                            <Typography variant="body2">
                              Tamaño: {power.size}
                            </Typography>
                          </div>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => {/* TODO: Download power */}}
                          color="primary"
                        >
                          <DownloadIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {/* TODO: Delete power */}}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cerrar
        </Button>
        <Button
          variant="contained"
          onClick={() => {/* TODO: Open edit modal */}}
        >
          Editar Usuario
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UserDetailModal;